<template>
  <div class="fnc-list-page">
    <header class="halloween-record-heading">
      <h1>Liste des FNC</h1>
      <p>Suivi des fiches de non-conformité</p>
    </header>
    <section class="fnc-summary-grid" aria-label="Résumé des FNC">
      <article class="fnc-summary-card">
        <span class="fnc-summary-icon fnc-summary-icon--all"><i class="fas fa-layer-group"></i></span>
        <div><strong>{{ filteredFncs.length }}</strong><span>Total</span></div>
      </article>
      <article class="fnc-summary-card">
        <span class="fnc-summary-icon fnc-summary-icon--open"><i class="fas fa-hourglass-half"></i></span>
        <div><strong>{{ openCount }}</strong><span>En cours</span></div>
      </article>
      <article class="fnc-summary-card">
        <span class="fnc-summary-icon fnc-summary-icon--closed"><i class="fas fa-check"></i></span>
        <div><strong>{{ closedCount }}</strong><span>Clôturées</span></div>
      </article>
      <article class="fnc-summary-card">
        <span class="fnc-summary-icon fnc-summary-icon--major"><i class="fas fa-exclamation"></i></span>
        <div><strong>{{ majorCount }}</strong><span>Majeures</span></div>
      </article>
    </section>

    <section class="fnc-toolbar-card">
      <div class="fnc-toolbar-main">
        <label class="fnc-search-field">
          <i class="fas fa-search" aria-hidden="true"></i>
          <input v-model.trim="search" type="search" placeholder="N°, produit, fournisseur, constat…" aria-label="Rechercher une FNC">
          <button v-if="search" type="button" class="fnc-clear-search" aria-label="Effacer la recherche" @click="search = ''">
            <i class="fas fa-times"></i>
          </button>
        </label>

        <div class="fnc-filter-field fnc-filter-field--element">
          <span>Statut</span>
          <el-select
            ref="statusFilter"
            v-model="selectedFilter"
            aria-label="Filtrer par statut"
            popper-class="fnc-advanced-popper fnc-toolbar-popper"
            @focus="closeAdvancedFilterPopups('statusFilter')"
          >
            <el-option label="Toutes les FNC" value="all"></el-option>
            <el-option label="En cours" value="in_progress"></el-option>
            <el-option label="Clôturées" value="closed"></el-option>
            <el-option label="Majeures" value="majeure"></el-option>
          </el-select>
        </div>

        <div class="fnc-filter-field fnc-filter-field--element fnc-year-filter">
          <span>Année</span>
          <el-select
            ref="yearFilter"
            v-model="selectedYear"
            aria-label="Filtrer par année"
            popper-class="fnc-advanced-popper fnc-toolbar-popper"
            @focus="closeAdvancedFilterPopups('yearFilter')"
          >
            <el-option label="Toutes" value="all"></el-option>
            <el-option
              v-for="year in availableYears"
              :key="year"
              :label="String(year)"
              :value="String(year)"
            ></el-option>
          </el-select>
        </div>
      </div>

      <div class="fnc-toolbar-actions">
        <button
          type="button"
          class="btn fnc-secondary-action fnc-advanced-toggle"
          :class="{ 'is-active': advancedFiltersOpen || activeAdvancedFilterCount > 0 }"
          :aria-expanded="advancedFiltersOpen ? 'true' : 'false'"
          aria-controls="fnc-advanced-filters"
          @click="advancedFiltersOpen = !advancedFiltersOpen"
        >
          <i class="fas fa-sliders-h" aria-hidden="true"></i>
          Filtres avancés
          <span v-if="activeAdvancedFilterCount" class="fnc-filter-count">{{ activeAdvancedFilterCount }}</span>
        </button>
        <button v-if="hasActiveFilters" type="button" class="btn fnc-secondary-action" @click="resetFilters">
          <i class="fas fa-undo-alt" aria-hidden="true"></i>
          Réinitialiser
        </button>
        <div class="fnc-view-toggle fnc-view-toggle--toolbar" role="group" aria-label="Mode d’affichage">
          <button
            type="button"
            :class="{ 'is-active': viewMode === 'cards' }"
            :aria-pressed="viewMode === 'cards' ? 'true' : 'false'"
            title="Affichage en cartes"
            @click="viewMode = 'cards'"
          >
            <i class="fas fa-th-large" aria-hidden="true"></i>
            <span>Cartes</span>
          </button>
          <button
            type="button"
            :class="{ 'is-active': viewMode === 'table' }"
            :aria-pressed="viewMode === 'table' ? 'true' : 'false'"
            title="Affichage en tableau"
            @click="viewMode = 'table'"
          >
            <i class="fas fa-table" aria-hidden="true"></i>
            <span>Tableau</span>
          </button>
        </div>
        <button
          type="button"
          class="btn fnc-secondary-action fnc-toolbar-icon-action"
          :disabled="isExportingFnc"
          :title="isExportingFnc ? 'Export en cours…' : 'Exporter les FNC filtrées'"
          :aria-label="isExportingFnc ? 'Export en cours' : 'Exporter les FNC filtrées'"
          @click="exportFnc"
        >
          <i class="fas" :class="isExportingFnc ? 'fa-circle-notch fa-spin' : 'fa-file-excel'" aria-hidden="true"></i>
          <span class="sr-only">{{ isExportingFnc ? 'Export en cours' : 'Exporter' }}</span>
        </button>
        <button type="button" class="btn fnc-primary-action fnc-toolbar-icon-action" title="Nouvelle FNC" aria-label="Nouvelle FNC" @click="addFnc">
          <i class="fas fa-plus" aria-hidden="true"></i>
          <span class="sr-only">Nouvelle FNC</span>
        </button>
      </div>

      <div v-show="advancedFiltersOpen" id="fnc-advanced-filters" class="fnc-advanced-filters">
        <div class="fnc-advanced-filter">
          <label for="fnc-supplier-filter">Fournisseur</label>
          <el-select
            ref="supplierFilter"
            id="fnc-supplier-filter"
            v-model="selectedSupplier"
            filterable
            clearable
            popper-class="fnc-advanced-popper"
            placeholder="Tous les fournisseurs"
            no-data-text="Aucun fournisseur"
            no-match-text="Aucun résultat"
            @focus="closeAdvancedFilterPopups('supplierFilter')"
          >
            <el-option
              v-for="supplier in supplierOptions"
              :key="supplier.value"
              :label="supplier.label"
              :value="supplier.value"
            ></el-option>
          </el-select>
        </div>

        <div class="fnc-advanced-filter">
          <label for="fnc-product-filter">Produit</label>
          <el-select
            ref="productFilter"
            id="fnc-product-filter"
            v-model="selectedProduct"
            filterable
            clearable
            popper-class="fnc-advanced-popper"
            placeholder="Tous les produits"
            no-data-text="Aucun produit"
            no-match-text="Aucun résultat"
            @focus="closeAdvancedFilterPopups('productFilter')"
          >
            <el-option
              v-for="product in productOptions"
              :key="product.value"
              :label="product.label"
              :value="product.value"
            ></el-option>
          </el-select>
        </div>

        <div class="fnc-advanced-filter">
          <label for="fnc-controller-filter">Responsable</label>
          <el-select
            ref="controllerFilter"
            id="fnc-controller-filter"
            v-model="selectedController"
            filterable
            clearable
            popper-class="fnc-advanced-popper"
            placeholder="Tous les responsables"
            no-data-text="Aucun responsable"
            no-match-text="Aucun résultat"
            @focus="closeAdvancedFilterPopups('controllerFilter')"
          >
            <el-option
              v-for="controller in controllerOptions"
              :key="controller.value"
              :label="controller.label"
              :value="controller.value"
            ></el-option>
          </el-select>
        </div>

        <div class="fnc-advanced-filter">
          <label for="fnc-importance-filter">Importance</label>
          <el-select
            ref="importanceFilter"
            id="fnc-importance-filter"
            v-model="selectedImportance"
            clearable
            popper-class="fnc-advanced-popper"
            placeholder="Toutes les importances"
            @focus="closeAdvancedFilterPopups('importanceFilter')"
          >
            <el-option label="Mineure" value="mineure"></el-option>
            <el-option label="Majeure" value="majeure"></el-option>
          </el-select>
        </div>

        <div class="fnc-advanced-filter">
          <label for="fnc-origin-filter">Origine</label>
          <el-select
            ref="originFilter"
            id="fnc-origin-filter"
            v-model="selectedOrigin"
            filterable
            clearable
            popper-class="fnc-advanced-popper"
            placeholder="Toutes les origines"
            no-data-text="Aucune origine"
            no-match-text="Aucun résultat"
            @focus="closeAdvancedFilterPopups('originFilter')"
          >
            <el-option
              v-for="origin in originOptions"
              :key="origin.value"
              :label="origin.label"
              :value="origin.value"
            ></el-option>
          </el-select>
        </div>

        <div class="fnc-advanced-filter fnc-advanced-filter--period">
          <label for="fnc-period-filter">Période de réception</label>
          <el-date-picker
            ref="periodFilter"
            id="fnc-period-filter"
            v-model="selectedPeriod"
            type="daterange"
            format="dd/MM/yyyy"
            value-format="yyyy-MM-dd"
            range-separator="au"
            start-placeholder="Date de début"
            end-placeholder="Date de fin"
            popper-class="fnc-advanced-popper"
            :picker-options="periodPickerOptions"
            :clearable="true"
            @focus="closeAdvancedFilterPopups('periodFilter')"
          ></el-date-picker>
        </div>

        <div class="fnc-advanced-filter fnc-advanced-filter--language">
          <label>Langue du PDF</label>
          <div class="fnc-pdf-language-group fnc-pdf-language-group--advanced" role="group" aria-label="Langue du PDF">
            <button
              v-for="language in ['FR', 'EN']"
              :key="language"
              type="button"
              :class="{ active: selectedLang === language }"
              :aria-pressed="selectedLang === language"
              @click="selectedLang = language"
            >
              {{ language }}
            </button>
          </div>
        </div>
      </div>
    </section>

    <div v-if="isLoading && viewMode === 'table'" class="fnc-table-shell" aria-label="Chargement des FNC" aria-busy="true">
      <div class="fnc-table-scroll">
        <table class="fnc-table fnc-table--loading">
          <thead>
            <tr>
              <th v-for="column in ['FNC', 'Statut', 'Produit / fournisseur', 'Réception', 'Responsable', 'Importance', 'Constat', 'Création', 'Actions']" :key="column" scope="col">{{ column }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in 8" :key="'table-skeleton-' + row">
              <td v-for="column in 9" :key="'table-skeleton-' + row + '-' + column">
                <span class="fnc-skeleton fnc-table-skeleton"></span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-else-if="isLoading" class="row fnc-card-grid" aria-label="Chargement des FNC" aria-busy="true">
      <div v-for="index in 8" :key="'skeleton-' + index" class="col-xl-3 col-lg-6 col-md-6 fnc-card-column">
        <article class="fnc-result-card fnc-result-card--skeleton">
          <header class="fnc-card-header">
            <span class="fnc-skeleton fnc-skeleton--reference"></span>
            <span class="fnc-skeleton fnc-skeleton--badge"></span>
          </header>
          <div class="fnc-card-content">
            <div class="fnc-card-product fnc-card-product--skeleton">
              <span class="fnc-skeleton fnc-skeleton--label"></span>
              <span class="fnc-skeleton fnc-skeleton--title"></span>
            </div>
            <div class="fnc-skeleton-facts">
              <span class="fnc-skeleton fnc-skeleton--fact"></span>
              <span class="fnc-skeleton fnc-skeleton--fact"></span>
              <span class="fnc-skeleton fnc-skeleton--fact"></span>
            </div>
            <div class="fnc-skeleton-cause-box">
              <span class="fnc-skeleton fnc-skeleton--cause"></span>
              <span class="fnc-skeleton fnc-skeleton--cause-short"></span>
            </div>
            <div class="fnc-skeleton-history">
              <span class="fnc-skeleton fnc-skeleton--history"></span>
              <span class="fnc-skeleton fnc-skeleton--history"></span>
            </div>
          </div>
          <footer class="fnc-card-footer">
            <div class="fnc-skeleton-actions">
              <span v-for="action in 5" :key="action" class="fnc-skeleton fnc-skeleton--action"></span>
            </div>
          </footer>
        </article>
      </div>
    </div>

    <div v-else-if="loadError" class="fnc-state-card fnc-state-card--error">
      <span><i class="fas fa-exclamation-triangle"></i></span>
      <h3>Impossible de charger les FNC</h3>
      <p>Une erreur est survenue pendant la récupération des données.</p>
      <button type="button" class="btn fnc-primary-action" @click="fetch">Réessayer</button>
    </div>

    <div v-else-if="filteredFncs.length === 0" class="fnc-state-card">
      <span><i class="fas fa-search"></i></span>
      <h3>Aucune FNC trouvée</h3>
      <p>Modifiez les filtres ou la recherche pour afficher d’autres résultats.</p>
      <button v-if="hasActiveFilters" type="button" class="btn fnc-secondary-action" @click="resetFilters">Réinitialiser les filtres</button>
    </div>

    <div v-else-if="viewMode === 'table'" class="fnc-table-shell">
      <div class="fnc-table-scroll" role="region" aria-label="Liste des FNC en tableau" tabindex="0">
        <table class="fnc-table">
          <caption class="sr-only">{{ resultTitle }}</caption>
          <thead>
            <tr>
              <th scope="col">FNC</th>
              <th scope="col">Statut</th>
              <th scope="col">Produit / fournisseur</th>
              <th scope="col">Réception</th>
              <th scope="col">Responsable</th>
              <th scope="col">Importance</th>
              <th scope="col">Constat</th>
              <th scope="col">Création</th>
              <th scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="fnc in filteredFncs" :key="'table-' + fnc.id">
              <td class="fnc-table-reference">
                <button type="button" @click="detailFnc(fnc.id)">{{ fnc.num }}</button>
                <small v-if="fnc.cost !== null && fnc.cost !== 0">{{ formatCost(fnc.cost) }}</small>
              </td>
              <td>
                <span class="fnc-badge" :class="fnc.archived_at ? 'fnc-badge--closed' : 'fnc-badge--open'">
                  <i class="fas" :class="fnc.archived_at ? 'fa-check-circle' : 'fa-clock'"></i>
                  {{ fnc.archived_at ? 'Clôturée' : 'En cours' }}
                </span>
              </td>
              <td class="fnc-table-product">
                <strong :title="productName(fnc)">{{ productName(fnc) }}</strong>
                <small :title="supplierName(fnc)">{{ supplierName(fnc) }}</small>
              </td>
              <td class="fnc-table-date">
                <strong>{{ formatShortDate(fnc.received_at) }}</strong>
                <small :title="valueOr(fnc.location)">{{ valueOr(fnc.location) }}</small>
              </td>
              <td>{{ valueOr(fnc.controller && fnc.controller.name, 'Non assigné') }}</td>
              <td>
                <span class="fnc-badge" :class="fnc.importance === 'majeure' ? 'fnc-badge--major' : 'fnc-badge--minor'">
                  {{ fnc.importance === 'majeure' ? 'Majeure' : (fnc.importance === 'mineure' ? 'Mineure' : 'Non renseignée') }}
                </span>
              </td>
              <td class="fnc-table-cause"><span :title="fnc.cause">{{ valueOr(fnc.cause) }}</span></td>
              <td class="fnc-table-date">
                <strong>{{ formatShortDate(fnc.created_at) }}</strong>
                <small :title="creatorName(fnc)">{{ creatorName(fnc) }}</small>
              </td>
              <td>
                <div class="fnc-card-actions fnc-table-actions" aria-label="Actions de la FNC">
                  <button type="button" title="Voir le détail" aria-label="Voir le détail" @click="detailFnc(fnc.id)"><i class="fas fa-eye"></i></button>
                  <button type="button" title="Envoyer par email" aria-label="Envoyer par email" @click="sendedFnc(fnc.id)"><i class="fas fa-paper-plane"></i></button>
                  <button type="button" title="Modifier" aria-label="Modifier" @click="updateFnc(fnc.id, 0)"><i class="fas fa-pen"></i></button>
                  <button type="button" title="Dupliquer" aria-label="Dupliquer" @click="cloneFnc(fnc.id)"><i class="fas fa-copy"></i></button>
                  <button type="button" title="Télécharger le PDF" aria-label="Télécharger le PDF" @click="showFnc(fnc.id)"><i class="fas fa-file-pdf"></i></button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-else class="row fnc-card-grid">
      <div v-for="fnc in filteredFncs" :key="fnc.id" class="col-xl-3 col-lg-6 col-md-6 fnc-card-column">
        <article class="fnc-result-card">
          <header class="fnc-card-header">
            <button type="button" class="fnc-reference" @click="detailFnc(fnc.id)">{{ fnc.num }}</button>
            <div class="fnc-card-badges">
              <span v-if="fnc.importance === 'majeure'" class="fnc-badge fnc-badge--major">Majeure</span>
              <span v-if="fnc.cost !== null && fnc.cost !== 0" class="fnc-badge fnc-badge--cost">{{ formatCost(fnc.cost) }}</span>
              <span class="fnc-badge" :class="fnc.archived_at ? 'fnc-badge--closed' : 'fnc-badge--open'">
                <i class="fas" :class="fnc.archived_at ? 'fa-check-circle' : 'fa-clock'"></i>
                {{ fnc.archived_at ? 'Clôturée' : 'En cours' }}
              </span>
            </div>
          </header>

          <div class="fnc-card-content">
            <div class="fnc-card-product">
              <h3 :title="productName(fnc)">{{ productName(fnc) }}</h3>
              <span class="fnc-overline">{{ supplierName(fnc) }}</span>
            </div>

            <div class="fnc-card-facts">
              <div class="fnc-card-fact fnc-card-fact--wide">
                <span class="fnc-fact-icon"><i class="fas fa-map-marker-alt"></i></span>
                <span>
                  <small>Lieu et date de réception</small>
                  <strong>{{ valueOr(fnc.location) }} le {{ formatShortDate(fnc.received_at) }}</strong>
                </span>
              </div>
              <div class="fnc-card-fact">
                <span class="fnc-fact-icon"><i class="fas fa-boxes"></i></span>
                <span><small>Quantité</small><strong>{{ valueOr(fnc.quantity) }}</strong></span>
              </div>
              <div class="fnc-card-fact">
                <span class="fnc-fact-icon"><i class="fas fa-chart-line"></i></span>
                <span><small>Efficacité</small><strong>{{ fnc.efficiency !== null ? fnc.efficiency : 'N/A' }}</strong></span>
              </div>
            </div>

            <div class="fnc-cause-block">
              <span>Constat</span>
              <p :title="fnc.cause">{{ fnc.cause }}</p>
            </div>

            <div class="fnc-history-block">
              <div class="fnc-history-row">
                <span class="fnc-history-icon"><i class="fas fa-plus"></i></span>
                <span>
                  <small>Créée le {{ formatShortDate(fnc.created_at) }}</small>
                  <strong>par {{ creatorName(fnc) }}</strong>
                </span>
              </div>
              <div v-if="fnc.archiver !== null" class="fnc-history-row fnc-history-row--closed">
                <span class="fnc-history-icon"><i class="fas fa-check"></i></span>
                <span>
                  <small>Clôturée le {{ formatShortDate(fnc.archived_at) }}</small>
                  <strong>par {{ archiverName(fnc) }}</strong>
                </span>
              </div>
            </div>
          </div>

          <footer class="fnc-card-footer">
            <div class="fnc-card-actions" aria-label="Actions de la FNC">
              <button type="button" title="Voir le détail" aria-label="Voir le détail" @click="detailFnc(fnc.id)"><i class="fas fa-eye"></i></button>
              <button type="button" title="Envoyer par email" aria-label="Envoyer par email" @click="sendedFnc(fnc.id)"><i class="fas fa-paper-plane"></i></button>
              <button type="button" title="Modifier" aria-label="Modifier" @click="updateFnc(fnc.id, 0)"><i class="fas fa-pen"></i></button>
              <button type="button" title="Dupliquer" aria-label="Dupliquer" @click="cloneFnc(fnc.id)"><i class="fas fa-copy"></i></button>
              <button type="button" title="Télécharger le PDF" aria-label="Télécharger le PDF" @click="showFnc(fnc.id)"><i class="fas fa-file-pdf"></i></button>
            </div>
          </footer>
        </article>
      </div>
    </div>

    <modal
      :show.sync="sendModal"
      modal-classes="fnc-email-dialog"
      header-classes="fnc-email-header"
      body-classes="fnc-email-body"
      :show-footer="false"
      @close="closeSendModal"
    >
      <div slot="header" class="fnc-email-heading">
        <span class="fnc-email-heading-icon"><i class="fas fa-paper-plane"></i></span>
        <div>
          <h4>Envoyer la FNC par email</h4>
          <p v-if="selectedFnc">{{ selectedFnc.num }} · PDF en {{ selectedLang === 'FR' ? 'français' : 'anglais' }}</p>
        </div>
      </div>

      <form class="fnc-email-form" @submit.prevent="sendFnc">
        <div class="fnc-form-group">
          <div class="fnc-label-row">
            <label for="fnc-email-recipients">Destinataires</label>
            <span>{{ validRecipientCount }}/20</span>
          </div>
          <div class="fnc-recipient-control">
            <div
              class="fnc-recipient-picker"
              :class="{ 'is-focused': emailSuggestionsOpen }"
              role="combobox"
              aria-haspopup="listbox"
              :aria-expanded="emailSuggestionsOpen ? 'true' : 'false'"
              aria-controls="fnc-email-suggestions"
              @click="focusRecipientInput"
            >
              <span
                v-for="receiver in mailForm.to"
                :key="receiver.toLowerCase()"
                class="fnc-recipient-chip"
                :title="receiver"
              >
                <span>{{ receiver }}</span>
                <button
                  type="button"
                  :aria-label="'Retirer ' + receiver"
                  @click.stop="removeEmailRecipient(receiver)"
                >
                  <i class="fas fa-times"></i>
                </button>
              </span>
              <input
                id="fnc-email-recipients"
                ref="recipientInput"
                v-model="emailDraft"
                type="email"
                autocomplete="off"
                :placeholder="mailForm.to.length ? 'Ajouter une adresse…' : 'Sélectionnez ou saisissez une adresse email'"
                @focus="emailSuggestionsOpen = true"
                @blur="handleRecipientBlur"
                @keydown="handleRecipientKeydown"
                @paste="handleRecipientPaste"
              >
            </div>

            <div
              v-if="emailSuggestionsOpen"
              id="fnc-email-suggestions"
              class="fnc-recipient-suggestions"
              role="listbox"
            >
              <button
                v-for="contact in filteredContactOptions"
                :key="contact.email"
                type="button"
                class="fnc-recipient-suggestion"
                role="option"
                @mousedown.prevent="addEmailRecipient(contact.email)"
              >
                <span class="fnc-recipient-avatar">{{ initials(contact.name || contact.email) }}</span>
                <span>
                  <strong>{{ contact.name || contact.email }}</strong>
                  <small v-if="contact.name">{{ contact.email }}</small>
                </span>
                <i class="fas fa-plus"></i>
              </button>

              <div v-if="!filteredContactOptions.length" class="fnc-recipient-empty">
                <template v-if="emailDraft">
                  <i class="fas fa-keyboard"></i>
                  <span v-if="isEmailDraftValid">Appuyez sur Entrée pour ajouter <strong>{{ emailDraft }}</strong></span>
                  <span v-else>Saisissez une adresse email valide.</span>
                </template>
                <template v-else>
                  <i class="fas fa-address-book"></i>
                  <span>Aucun autre contact disponible.</span>
                </template>
              </div>
            </div>
          </div>
          <p class="fnc-field-help"><i class="fas fa-info-circle"></i> Saisissez une adresse puis appuyez sur Entrée pour l’ajouter manuellement.</p>
        </div>

        <div class="fnc-email-privacy">
          <i class="fas fa-user-shield"></i>
          <div>
            <strong>Confidentialité des destinataires</strong>
            <p>La première adresse reçoit le mail. Les autres sont ajoutées en copie cachée et ne voient pas les autres destinataires.</p>
          </div>
        </div>

        <div class="fnc-form-group">
          <div class="fnc-label-row">
            <label for="fnc-email-subject">Objet</label>
            <span>{{ mailForm.object.length }}/255</span>
          </div>
          <input id="fnc-email-subject" v-model.trim="mailForm.object" type="text" maxlength="255" class="fnc-form-control">
        </div>

        <div class="fnc-form-group">
          <div class="fnc-label-row">
            <label for="fnc-email-content">Message <small>(facultatif)</small></label>
            <span>{{ mailForm.content.length }}/10 000</span>
          </div>
          <textarea id="fnc-email-content" v-model="mailForm.content" maxlength="10000" rows="6" class="fnc-form-control" placeholder="Ajoutez un message qui apparaîtra dans l’email…"></textarea>
        </div>

        <div v-if="selectedFnc && !selectedFnc.archived_at" class="fnc-form-group">
          <label>
            <input type="checkbox" v-model="mailForm.request_completion">
            Demander au fournisseur de compléter la FNC
          </label>
          <p>Ajoute au mail un lien vers le formulaire fournisseur, y compris pour une FNC mineure.</p>
        </div>

        <div class="fnc-email-actions">
          <button type="button" class="btn fnc-secondary-action" :disabled="isSendingFnc" @click="closeSendModal">Annuler</button>
          <button type="submit" class="btn fnc-primary-action" :disabled="isSendingFnc">
            <i class="fas" :class="isSendingFnc ? 'fa-circle-notch fa-spin' : 'fa-paper-plane'"></i>
            {{ isSendingFnc ? 'Envoi en cours…' : 'Envoyer la FNC' }}
          </button>
        </div>
      </form>
    </modal>
  </div>
</template>

<script>
import moment from 'moment'
import basic from '../../../mixins/basic'
import { Modal } from 'src/components'
import authorizable from '@/mixins/authorizable'
import { DatePicker, Option, Select } from 'element-ui'

const FNC_FILTERS_STORAGE_PREFIX = 'process-v2.fnc-list.filters.v1'
const FNC_STATUS_FILTERS = ['all', 'in_progress', 'closed', 'majeure']

export default {
  name: 'FncList',
  mixins: [basic, authorizable],
  components: {
    Modal,
    [DatePicker.name]: DatePicker,
    [Option.name]: Option,
    [Select.name]: Select
  },
  data() {
    return {
      selectedFilter: 'all',
      selectedYear: 'all',
      selectedSupplier: null,
      selectedProduct: null,
      selectedController: null,
      selectedImportance: null,
      selectedOrigin: null,
      selectedPeriod: null,
      periodPickerOptions: {
        firstDayOfWeek: 1
      },
      viewMode: 'cards',
      advancedFiltersOpen: false,
      selectedFnc: null,
      fncList: [],
      fncsBuffer: [],
      selectedLang: 'FR',
      search: '',
      sendModal: false,
      emailDraft: '',
      emailSuggestionsOpen: false,
      mailForm: {
        id: '',
        to: [],
        object: '',
        content: '',
        request_completion: false
      },
      isLoading: true,
      loadError: false,
      isSendingFnc: false,
      isExportingFnc: false,
      autenticatedUser: '',
      departments: [],
      isManager: false
    }
  },
  computed: {
    filteredFncs() {
      const query = this.normalize(this.search)

      return this.fncsBuffer.filter(fnc => {
        const matchesStatus = this.selectedFilter === 'all'
          || (this.selectedFilter === 'in_progress' && !fnc.archived_at)
          || (this.selectedFilter === 'closed' && !!fnc.archived_at)
          || (this.selectedFilter === 'majeure' && fnc.importance === 'majeure')

        const matchesYear = this.selectedYear === 'all'
          || String(this.dateYear(fnc.created_at)) === this.selectedYear

        const matchesSupplier = !this.selectedSupplier
          || String(fnc.supplier && fnc.supplier.id) === this.selectedSupplier
        const matchesProduct = !this.selectedProduct
          || String(fnc.product && fnc.product.id) === this.selectedProduct
        const matchesController = !this.selectedController
          || String(fnc.controller && fnc.controller.id) === this.selectedController
        const matchesImportance = !this.selectedImportance
          || fnc.importance === this.selectedImportance
        const matchesOrigin = !this.selectedOrigin
          || String(fnc.origin) === this.selectedOrigin
        const matchesPeriod = this.matchesSelectedPeriod(fnc.received_at)

        if (!matchesStatus || !matchesYear || !matchesSupplier || !matchesProduct
          || !matchesController || !matchesImportance || !matchesOrigin || !matchesPeriod) return false

        if (!query) return true

        const searchable = [
          fnc.num,
          fnc.importance,
          fnc.archived_at ? 'clôturée fermé' : 'en cours ouverte',
          fnc.product && fnc.product.name,
          fnc.product && fnc.product.reference,
          fnc.supplier && fnc.supplier.name,
          fnc.supplier && fnc.supplier.num,
          fnc.location,
          fnc.controller && fnc.controller.name,
          fnc.cause,
          fnc.creator && fnc.creator.name,
          this.formatShortDate(fnc.created_at)
        ].map(this.normalize).join(' ')

        return searchable.indexOf(query) !== -1
      })
    },
    availableYears() {
      const years = this.fncsBuffer
        .map(fnc => this.dateYear(fnc.created_at))
        .filter(year => !!year)

      return Array.from(new Set(years)).sort((a, b) => b - a)
    },
    supplierOptions() {
      return this.relationFilterOptions('supplier', supplier => {
        return [supplier.num, supplier.name].filter(Boolean).join(' — ')
      })
    },
    productOptions() {
      return this.relationFilterOptions('product', product => {
        return [product.reference, product.name].filter(Boolean).join(' — ')
      })
    },
    controllerOptions() {
      return this.relationFilterOptions('controller', controller => controller.name)
    },
    originOptions() {
      const origins = this.fncsBuffer
        .map(fnc => fnc.origin)
        .filter(origin => origin !== null && origin !== undefined && origin !== '')
        .map(origin => String(origin))

      return Array.from(new Set(origins))
        .sort((a, b) => a.localeCompare(b, 'fr', { sensitivity: 'base' }))
        .map(origin => ({ value: origin, label: origin }))
    },
    yearFilteredFncs() {
      if (this.selectedYear === 'all') return this.fncsBuffer

      return this.fncsBuffer.filter(fnc => String(this.dateYear(fnc.created_at)) === this.selectedYear)
    },
    openCount() {
      return this.filteredFncs.filter(fnc => !fnc.archived_at).length
    },
    closedCount() {
      return this.filteredFncs.filter(fnc => !!fnc.archived_at).length
    },
    majorCount() {
      return this.filteredFncs.filter(fnc => fnc.importance === 'majeure').length
    },
    hasActiveFilters() {
      return !!this.search || this.selectedFilter !== 'all' || this.selectedYear !== 'all'
        || this.activeAdvancedFilterCount > 0
    },
    activeAdvancedFilterCount() {
      return [
        this.selectedSupplier,
        this.selectedProduct,
        this.selectedController,
        this.selectedImportance,
        this.selectedOrigin,
        Array.isArray(this.selectedPeriod) && this.selectedPeriod.length === 2
      ].filter(Boolean).length
    },
    resultTitle() {
      if (this.selectedFilter === 'in_progress') return 'FNC en cours'
      if (this.selectedFilter === 'closed') return 'FNC clôturées'
      if (this.selectedFilter === 'majeure') return 'FNC majeures'
      return 'Toutes les FNC'
    },
    contactOptions() {
      if (!this.selectedFnc || !Array.isArray(this.selectedFnc.contacts)) return []
      const seenEmails = []

      return this.selectedFnc.contacts.filter(contact => {
        if (!contact || !this.isValidEmail(contact.email)) return false
        const email = String(contact.email).trim().toLowerCase()
        if (seenEmails.indexOf(email) !== -1) return false
        seenEmails.push(email)
        return true
      })
    },
    filteredContactOptions() {
      const query = this.normalize(this.emailDraft)
      const selectedEmails = this.mailForm.to.map(receiver => String(receiver).toLowerCase())

      return this.contactOptions.filter(contact => {
        const email = String(contact.email).trim()
        if (selectedEmails.indexOf(email.toLowerCase()) !== -1) return false
        if (!query) return true

        return this.normalize((contact.name || '') + ' ' + email).indexOf(query) !== -1
      })
    },
    isEmailDraftValid() {
      return this.isValidEmail(this.emailDraft)
    },
    validRecipientCount() {
      return this.mailForm.to.filter(receiver => typeof receiver === 'string' && receiver.trim()).length
    }
  },
  watch: {
    search: 'persistFilters',
    selectedFilter: 'persistFilters',
    selectedYear: 'persistFilters',
    selectedSupplier: 'persistFilters',
    selectedProduct: 'persistFilters',
    selectedController: 'persistFilters',
    selectedImportance: 'persistFilters',
    selectedOrigin: 'persistFilters',
    viewMode: 'persistFilters',
    selectedPeriod: {
      handler: 'persistFilters',
      deep: true
    }
  },
  mounted() {
    moment.locale('fr')
    this.fetch()

    EventBus.$on('isAutenticated', this.handleAuthenticatedUser)
    if (window.App !== undefined) this.handleAuthenticatedUser()

    this.axios.get(process.env.VUE_APP_API_BASE_URL + 'fnc/departments/list').then(response => {
      this.departments = response.data
      this.isManager = this.departments.some(department => this.autenticatedUser && department.manager_id == this.autenticatedUser.id)
    })
  },
  beforeDestroy() {
    EventBus.$off('isAutenticated', this.handleAuthenticatedUser)
  },
  methods: {
    closeAdvancedFilterPopups(activeRef) {
      const filterRefs = [
        'statusFilter',
        'yearFilter',
        'supplierFilter',
        'productFilter',
        'controllerFilter',
        'importanceFilter',
        'originFilter',
        'periodFilter'
      ]

      filterRefs.forEach(refName => {
        const filter = this.$refs[refName]
        if (refName === activeRef || !filter) return

        if (typeof filter.handleClose === 'function') {
          filter.handleClose()
        } else if (typeof filter.blur === 'function') {
          filter.blur()
        }
      })
    },
    handleAuthenticatedUser() {
      this.autenticatedUser = window.App || ''
      this.restoreFilters()
    },
    filterStorageKey() {
      const globalUser = typeof window !== 'undefined' ? window.App : null
      const user = this.autenticatedUser || globalUser

      if (!user || user.id === null || user.id === undefined) return null

      return FNC_FILTERS_STORAGE_PREFIX + '.user-' + user.id
    },
    persistFilters() {
      const storageKey = this.filterStorageKey()
      if (!storageKey || typeof window === 'undefined') return

      try {
        window.localStorage.setItem(storageKey, JSON.stringify({
          search: this.search,
          selectedFilter: this.selectedFilter,
          selectedYear: this.selectedYear,
          selectedSupplier: this.selectedSupplier,
          selectedProduct: this.selectedProduct,
          selectedController: this.selectedController,
          selectedImportance: this.selectedImportance,
          selectedOrigin: this.selectedOrigin,
          selectedPeriod: this.selectedPeriod,
          viewMode: this.viewMode
        }))
      } catch (error) {
        // La liste reste utilisable si le stockage local est désactivé ou saturé.
      }
    },
    restoreFilters() {
      const storageKey = this.filterStorageKey()
      if (!storageKey || typeof window === 'undefined') return

      try {
        const savedFilters = window.localStorage.getItem(storageKey)
        if (!savedFilters) {
          this.persistFilters()
          return
        }

        const filters = JSON.parse(savedFilters)
        const selectedYear = String(filters.selectedYear || '')
        const optionalFilterValue = value => {
          return typeof value === 'string' || typeof value === 'number' ? String(value) : null
        }

        if (typeof filters.search === 'string') this.search = filters.search
        if (FNC_STATUS_FILTERS.indexOf(filters.selectedFilter) !== -1) this.selectedFilter = filters.selectedFilter
        if (selectedYear === 'all' || /^\d{4}$/.test(selectedYear)) this.selectedYear = selectedYear
        this.selectedSupplier = optionalFilterValue(filters.selectedSupplier)
        this.selectedProduct = optionalFilterValue(filters.selectedProduct)
        this.selectedController = optionalFilterValue(filters.selectedController)
        this.selectedOrigin = optionalFilterValue(filters.selectedOrigin)
        if (['cards', 'table'].indexOf(filters.viewMode) !== -1) this.viewMode = filters.viewMode
        this.selectedImportance = ['mineure', 'majeure'].indexOf(filters.selectedImportance) !== -1
          ? filters.selectedImportance
          : null

        if (Array.isArray(filters.selectedPeriod)
          && filters.selectedPeriod.length === 2
          && filters.selectedPeriod.every(date => /^\d{4}-\d{2}-\d{2}$/.test(date))) {
          this.selectedPeriod = filters.selectedPeriod.slice()
        } else {
          this.selectedPeriod = null
        }

        this.advancedFiltersOpen = this.activeAdvancedFilterCount > 0
      } catch (error) {
        // Une préférence invalide ne doit jamais empêcher l'affichage de la liste.
      }
    },
    fetch() {
      this.isLoading = true
      this.loadError = false

      this.axios.get(process.env.VUE_APP_API_BASE_URL + 'fnc/list').then(response => {
        this.fncList = response.data
        this.fncsBuffer = response.data
        if (this.$route.query.sendCopy) {
          this.sendedFnc(this.$route.query.sendCopy)
          this.$router.replace({ name: 'fnc list', query: {} })
        }
      }).catch(() => {
        this.loadError = true
      }).then(() => {
        this.isLoading = false
      })
    },
    normalize(value) {
      return String(value || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
    },
    relationFilterOptions(relationName, labelFormatter) {
      const optionsById = {}

      this.fncsBuffer.forEach(fnc => {
        const relation = fnc[relationName]
        if (!relation || relation.id === null || relation.id === undefined) return

        const value = String(relation.id)
        if (optionsById[value]) return

        optionsById[value] = {
          value,
          label: labelFormatter(relation) || value
        }
      })

      return Object.keys(optionsById)
        .map(id => optionsById[id])
        .sort((a, b) => a.label.localeCompare(b.label, 'fr', { sensitivity: 'base' }))
    },
    matchesSelectedPeriod(date) {
      if (!Array.isArray(this.selectedPeriod) || this.selectedPeriod.length !== 2) return true

      const dateKey = this.filterDateKey(date)
      if (!dateKey) return false

      return dateKey >= this.selectedPeriod[0] && dateKey <= this.selectedPeriod[1]
    },
    filterDateKey(date) {
      if (!date) return null

      const parsed = moment(date)
      return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null
    },
    dateYear(date) {
      if (!date) return null
      const parsed = new Date(date)
      return Number.isNaN(parsed.getTime()) ? null : parsed.getFullYear()
    },
    formatShortDate(date) {
      if (!date) return '—'
      const parsed = new Date(date)
      return Number.isNaN(parsed.getTime()) ? '—' : parsed.toLocaleDateString('fr-FR')
    },
    valueOr(value, fallback) {
      return value !== null && value !== undefined && value !== '' ? value : (fallback || '—')
    },
    supplierName(fnc) {
      return fnc.supplier && fnc.supplier.num && fnc.supplier.name ? fnc.supplier.name : 'Fournisseur non spécifié'
    },
    productName(fnc) {
      return fnc.product && fnc.product.name ? fnc.product.name : 'Produit non spécifié'
    },
    productReference(fnc) {
      return fnc.product && fnc.product.reference ? 'Réf. ' + fnc.product.reference : 'Référence non renseignée'
    },
    creatorName(fnc) {
      return fnc.creator && fnc.creator.name ? fnc.creator.name : 'Inconnu'
    },
    archiverName(fnc) {
      return fnc.archiver && fnc.archiver.name ? fnc.archiver.name : 'Inconnu'
    },
    formatCost(cost) {
      if (cost === null || cost === undefined || cost === '') return '—'
      return cost + ' €'
    },
    initials(name) {
      return String(name || '?').split(' ').filter(Boolean).slice(0, 2).map(part => part.charAt(0)).join('').toUpperCase()
    },
    resetFilters() {
      this.search = ''
      this.selectedFilter = 'all'
      this.selectedYear = 'all'
      this.selectedSupplier = null
      this.selectedProduct = null
      this.selectedController = null
      this.selectedImportance = null
      this.selectedOrigin = null
      this.selectedPeriod = null
      this.$nextTick(this.persistFilters)
    },
    togglePdfLanguage() {
      this.selectedLang = this.selectedLang === 'FR' ? 'EN' : 'FR'
    },
    isValidEmail(email) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || '').trim())
    },
    focusRecipientInput() {
      if (this.$refs.recipientInput) this.$refs.recipientInput.focus()
    },
    addEmailRecipient(email, showInvalidMessage = true) {
      const receiver = String(email || '').trim().replace(/[;,]+$/, '')
      if (!receiver) return false

      if (!this.isValidEmail(receiver)) {
        if (showInvalidMessage) {
          this.$notify({ message: 'Cette adresse email n’est pas valide', type: 'danger', verticalAlign: 'bottom' })
        }
        return false
      }

      const alreadySelected = this.mailForm.to.some(value => String(value).toLowerCase() === receiver.toLowerCase())
      if (alreadySelected) {
        this.emailDraft = ''
        return false
      }

      if (this.mailForm.to.length >= 20) {
        this.$notify({ message: 'Vous pouvez ajouter au maximum 20 destinataires', type: 'warning', verticalAlign: 'bottom' })
        return false
      }

      this.mailForm.to.push(receiver)
      this.emailDraft = ''
      this.emailSuggestionsOpen = true
      this.$nextTick(this.focusRecipientInput)
      return true
    },
    removeEmailRecipient(receiver) {
      const receiverIndex = this.mailForm.to.findIndex(value => String(value).toLowerCase() === String(receiver).toLowerCase())
      if (receiverIndex !== -1) this.mailForm.to.splice(receiverIndex, 1)
      this.$nextTick(this.focusRecipientInput)
    },
    handleRecipientKeydown(event) {
      if (event.key === 'Enter' || event.key === ',' || event.key === ';') {
        event.preventDefault()
        this.addEmailRecipient(this.emailDraft)
        return
      }

      if (event.key === 'Backspace' && !this.emailDraft && this.mailForm.to.length) {
        this.mailForm.to.pop()
      }

      if (event.key === 'Escape') {
        this.emailSuggestionsOpen = false
      }
    },
    handleRecipientPaste(event) {
      const pastedText = event.clipboardData && event.clipboardData.getData('text')
      if (!pastedText) return

      const receivers = pastedText.split(/[\s,;]+/).map(receiver => receiver.trim()).filter(Boolean)
      if (receivers.length < 2) return

      event.preventDefault()
      receivers.forEach(receiver => this.addEmailRecipient(receiver))
    },
    handleRecipientBlur() {
      if (this.isValidEmail(this.emailDraft)) this.addEmailRecipient(this.emailDraft, false)
      window.setTimeout(() => {
        this.emailSuggestionsOpen = false
      }, 120)
    },
    addFnc() {
      this.$router.push({ name: 'fnc form' })
    },
    detailFnc(id) {
      this.$router.push({ name: 'fnc', params: { id } })
    },
    sendedFnc(id) {
      this.selectedFnc = this.fncsBuffer.find(fnc => fnc.id == id) || null
      if (!this.selectedFnc) return

      const defaultContact = (this.selectedFnc.contacts || []).find(contact => contact.email)
      this.mailForm = {
        id: this.selectedFnc.id,
        to: defaultContact ? [defaultContact.email] : [],
        object: 'Fiche de non-conformité ' + this.selectedFnc.num,
        content: this.selectedFnc.archived_at ? 'Veuillez trouver ci-joint une copie de la FNC clôturée.' : '',
        request_completion: !this.selectedFnc.archived_at && this.selectedFnc.importance === 'majeure'
      }
      this.emailDraft = ''
      this.emailSuggestionsOpen = false
      this.sendModal = true
    },
    closeSendModal() {
      if (this.isSendingFnc) return
      this.emailDraft = ''
      this.emailSuggestionsOpen = false
      this.sendModal = false
    },
    sendFnc() {
      if (this.isSendingFnc) return

      const receivers = this.mailForm.to
        .map(receiver => typeof receiver === 'string' ? receiver.trim() : '')
        .filter(receiver => receiver !== '')
        .filter((receiver, index, values) => values.findIndex(value => value.toLowerCase() === receiver.toLowerCase()) === index)

      if (receivers.length === 0) {
        this.$notify({ message: 'Veuillez sélectionner au moins un destinataire', type: 'danger', verticalAlign: 'bottom' })
        return
      }

      this.isSendingFnc = true
      this.axios.post(process.env.VUE_APP_API_BASE_URL + 'fnc/send', {
        id: this.mailForm.id,
        receiver: receivers,
        object: this.mailForm.object,
        body: this.mailForm.content,
        selectedLang: this.selectedLang,
        request_completion: !!this.mailForm.request_completion
      }).then(() => {
        this.sendModal = false
        this.emailDraft = ''
        this.emailSuggestionsOpen = false
        this.mailForm = { id: '', to: [], object: '', content: '' }
        this.$notify({ message: 'La fiche a bien été envoyée par e-mail', type: 'success', verticalAlign: 'bottom' })
      }).catch(error => {
        const errors = error.response && error.response.data && error.response.data.errors
        const receiverError = errors && errors.receiver && errors.receiver[0]
        this.$notify({ message: receiverError || "L'envoi de la fiche a échoué", type: 'danger', verticalAlign: 'bottom' })
      }).then(() => {
        this.isSendingFnc = false
      })
    },
    cloneFnc(id, type) {
      this.$router.push({ name: 'fnc form', params: { id, type } })
    },
    updateFnc(id, type) {
      this.$router.push({ name: 'fnc form', params: { id, type } })
    },
    showFnc(id) {
      this.selectedFnc = this.fncsBuffer.find(fnc => fnc.id == id) || null
      if (!this.selectedFnc) return

      this.axios.get(process.env.VUE_APP_API_BASE_URL + 'pdf', {
        params: { id: this.selectedFnc.id, selectedLang: this.selectedLang },
        responseType: 'arraybuffer',
        headers: { Accept: 'application/pdf' }
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', this.selectedFnc.num + '.pdf')
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        this.$notify({ message: 'Téléchargement du PDF en cours…', type: 'success', verticalAlign: 'bottom' })
      })
    },
    exportFnc() {
      if (this.isExportingFnc) return

      const filteredFncIds = this.filteredFncs
        .map(fnc => fnc.id)
        .filter(id => id !== null && id !== undefined)

      if (!filteredFncIds.length) {
        this.$notify({ message: 'Aucune FNC à exporter avec les filtres actuels', type: 'warning', verticalAlign: 'bottom' })
        return
      }

      this.isExportingFnc = true

      this.axios({
        url: process.env.VUE_APP_API_BASE_URL + 'fnc/export',
        method: 'POST',
        responseType: 'blob',
        data: {
          filter: this.selectedFilter,
          ids: filteredFncIds
        }
      }).then(response => {
        const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
        const downloadLink = document.createElement('a')
        const url = window.URL.createObjectURL(blob)
        downloadLink.href = url
        downloadLink.download = 'Liste des FNC.xlsx'
        downloadLink.click()
        window.URL.revokeObjectURL(url)
        const exportLabel = filteredFncIds.length > 1 ? 'FNC ont été exportées' : 'FNC a été exportée'
        this.$notify({ message: filteredFncIds.length + ' ' + exportLabel, type: 'success', verticalAlign: 'bottom' })
      }).catch(() => {
        this.$notify({ message: "L'export des FNC a échoué", type: 'danger', verticalAlign: 'bottom' })
      }).then(() => {
        this.isExportingFnc = false
      })
    }
  }
}
</script>

<style>
.fnc-list-page {
  --fnc-primary: #f96332;
  --fnc-primary-dark: #dc4c1d;
  --fnc-ink: #233044;
  --fnc-muted: #738096;
  --fnc-border: #e6eaf0;
  --fnc-surface: #ffffff;
  padding-bottom: 24px;
}

.fnc-page-header,
.fnc-toolbar-card,
.fnc-card-header,
.fnc-card-footer,
.fnc-toolbar-main,
.fnc-toolbar-actions,
.fnc-created-by,
.fnc-email-actions,
.fnc-label-row {
  display: flex;
  align-items: center;
}

.fnc-page-header {
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 20px;
}

.fnc-eyebrow {
  color: var(--fnc-primary);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .11em;
  margin-bottom: 5px;
  text-transform: uppercase;
}

.fnc-page-header h1 {
  color: var(--fnc-ink);
  font-size: 28px;
  font-weight: 700;
  letter-spacing: -.02em;
  margin: 0 0 5px;
}

.fnc-page-header p,
.fnc-state-card p {
  color: var(--fnc-muted);
  margin: 0;
}

.fnc-primary-action,
.fnc-secondary-action {
  align-items: center;
  border-radius: 9px;
  display: inline-flex;
  font-size: 12px;
  font-weight: 600;
  gap: 8px;
  justify-content: center;
  margin: 0;
  min-height: 40px;
  padding: 10px 15px;
  text-transform: none;
}

.fnc-primary-action {
  background: var(--fnc-primary);
  border: 1px solid var(--fnc-primary);
  box-shadow: 0 7px 16px rgba(249, 99, 50, .19);
  color: #fff;
}

.fnc-primary-action:hover,
.fnc-primary-action:focus {
  background: var(--fnc-primary-dark);
  border-color: var(--fnc-primary-dark);
  color: #fff;
}

.fnc-secondary-action {
  background: #fff;
  border: 1px solid var(--fnc-border);
  box-shadow: none;
  color: #526078;
}

.fnc-secondary-action:hover,
.fnc-secondary-action:focus {
  background: #fff7f4;
  border-color: #f8b49e;
  color: var(--fnc-primary-dark);
}

.fnc-summary-grid {
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  margin-bottom: 18px;
}

.fnc-summary-card {
  align-items: center;
  background: var(--fnc-surface);
  border: 1px solid var(--fnc-border);
  border-radius: 12px;
  box-shadow: 0 5px 16px rgba(36, 48, 68, .045);
  display: flex;
  gap: 13px;
  min-height: 78px;
  padding: 14px 16px;
}

.fnc-summary-icon {
  align-items: center;
  border-radius: 10px;
  display: inline-flex;
  height: 44px;
  justify-content: center;
  width: 44px;
}

.fnc-summary-icon--all { background: #edf3ff; color: #3d6dcc; }
.fnc-summary-icon--open { background: #fff5df; color: #b87a06; }
.fnc-summary-icon--closed { background: #eaf8ef; color: #26834b; }
.fnc-summary-icon--major { background: #fff0f0; color: #c54242; }

.fnc-summary-card div {
  display: flex;
  flex-direction: column;
}

.fnc-summary-card strong {
  color: var(--fnc-ink);
  font-size: 22px;
  line-height: 1.1;
}

.fnc-summary-card span:last-child {
  color: var(--fnc-muted);
  font-size: 11px;
  margin-top: 4px;
}

.fnc-toolbar-card {
  background: var(--fnc-surface);
  border: 1px solid var(--fnc-border);
  border-radius: 12px;
  box-shadow: 0 5px 18px rgba(36, 48, 68, .045);
  flex-wrap: wrap;
  gap: 16px;
  justify-content: space-between;
  margin-bottom: 22px;
  padding: 13px;
}

.fnc-toolbar-main {
  flex: 1;
  gap: 11px;
  min-width: 0;
}

.fnc-toolbar-actions { gap: 8px; }

.fnc-advanced-toggle.is-active {
  background: #fff3ef;
  border-color: #f8b49e;
  color: var(--fnc-primary-dark);
}

.fnc-filter-count {
  align-items: center;
  background: var(--fnc-primary);
  border-radius: 999px;
  color: #fff;
  display: inline-flex;
  font-size: 9px;
  font-weight: 700;
  height: 18px;
  justify-content: center;
  min-width: 18px;
  padding: 0 5px;
}

.fnc-advanced-filters {
  border-top: 1px solid #edf0f4;
  display: grid;
  flex: 0 0 100%;
  gap: 9px;
  grid-template-columns:
    minmax(190px, 1.15fr)
    minmax(190px, 1.15fr)
    minmax(165px, 1fr)
    minmax(140px, .75fr)
    minmax(165px, .9fr)
    minmax(280px, 1.5fr)
    minmax(105px, .55fr);
  overflow-x: auto;
  padding: 11px 1px 4px;
  scrollbar-color: #d6dce5 transparent;
  scrollbar-width: thin;
}

.fnc-advanced-filter {
  min-width: 0;
  padding-top: 4px;
  position: relative;
}

.fnc-advanced-filter > label {
  background: #fff;
  color: #737f91;
  font-size: 9px;
  font-weight: 700;
  left: 9px;
  line-height: 1;
  margin: 0;
  padding: 0 4px;
  position: absolute;
  top: 0;
  text-transform: uppercase;
  z-index: 2;
}

.fnc-advanced-filter .el-select,
.fnc-advanced-filter .el-date-editor {
  width: 100% !important;
}

.fnc-list-page .fnc-advanced-filter .el-input__inner,
.fnc-list-page .fnc-advanced-filter .el-range-editor.el-input__inner {
  background: #fff !important;
  border: 1px solid #d8dde5 !important;
  border-radius: 8px !important;
  box-shadow: none !important;
  color: #2c2c2c !important;
  font-size: 12px !important;
  font-weight: 400 !important;
  height: 36px !important;
  line-height: 34px !important;
  margin: 0 !important;
  padding: 0 31px 0 11px !important;
  text-align: left !important;
  text-transform: none !important;
}

.fnc-list-page .fnc-advanced-filter .el-range-editor.el-input__inner {
  padding: 0 8px !important;
}

.fnc-list-page .fnc-advanced-filter .el-input__inner::placeholder,
.fnc-list-page .fnc-advanced-filter .el-range-input::placeholder {
  color: #9aa4b2 !important;
  opacity: 1;
}

.fnc-list-page .fnc-advanced-filter .el-input.is-focus .el-input__inner,
.fnc-list-page .fnc-advanced-filter .el-range-editor.is-active {
  border-color: var(--fnc-primary) !important;
}

.fnc-list-page .fnc-advanced-filter .el-input__icon,
.fnc-list-page .fnc-advanced-filter .el-range__icon,
.fnc-list-page .fnc-advanced-filter .el-range__close-icon {
  line-height: 34px !important;
}

.fnc-list-page .fnc-advanced-filter .el-select__caret {
  color: #8b95a5 !important;
}

.fnc-list-page .fnc-advanced-filter .el-range-input {
  color: #2c2c2c;
  font-size: 12px;
}

.fnc-list-page .fnc-advanced-filter .el-range-separator {
  color: #8b95a5;
  line-height: 30px;
  width: 24px;
}

.fnc-advanced-popper {
  background: #fff !important;
  border: 1px solid #e1e5eb !important;
  border-radius: 8px !important;
  box-shadow: 0 12px 30px rgba(35, 48, 68, .16) !important;
  margin-top: 6px !important;
  z-index: 12050 !important;
}

.el-picker-panel.fnc-advanced-popper,
.fnc-advanced-popper .el-picker-panel__body,
.fnc-advanced-popper .el-date-range-picker__content {
  background: #fff !important;
  opacity: 1 !important;
}

.fnc-advanced-popper .el-date-range-picker__content.is-left {
  border-right-color: #edf0f4;
}

.fnc-advanced-popper .el-select-dropdown__list {
  padding: 5px 0;
}

.fnc-advanced-popper .el-select-dropdown__item {
  color: #465266;
  font-size: 12px;
  height: 34px;
  line-height: 34px;
  padding: 0 12px;
}

.fnc-advanced-popper .el-select-dropdown__item.hover,
.fnc-advanced-popper .el-select-dropdown__item:hover {
  background: #fff4f0;
}

.fnc-advanced-popper .el-select-dropdown__item.selected {
  color: #f96332;
  font-weight: 700;
}

.fnc-search-field {
  align-items: center;
  background: #fff;
  border: 1px solid #d8dde5;
  border-radius: 8px;
  display: flex;
  flex: 1;
  height: 40px;
  margin: 0;
  min-width: 230px;
  padding: 0;
  position: relative;
  transition: .2s ease;
}

.fnc-search-field:focus-within {
  background: #fff;
  border-color: #f96332;
  box-shadow: none;
}

.fnc-search-field > i {
  color: #8b95a5;
  font-size: 12px;
  left: 12px;
  pointer-events: none;
  position: absolute;
}

.fnc-search-field input {
  background: transparent;
  border: 0;
  color: var(--fnc-ink);
  flex: 1;
  font-size: 12px;
  height: 38px;
  min-width: 0;
  outline: none;
  padding: 0 34px;
  width: 100%;
}

.fnc-clear-search {
  background: none;
  border: 0;
  color: #9ba5b5;
  cursor: pointer;
  padding: 5px;
  position: absolute;
  right: 6px;
}

.fnc-filter-field {
  align-items: center;
  border: 1px solid var(--fnc-border);
  border-radius: 8px;
  display: flex;
  min-width: 155px;
  padding-left: 11px;
}

.fnc-filter-field > span {
  color: #919bae;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
}

.fnc-filter-field .el-select { flex: 1; }
.fnc-filter-field .el-input__inner { border: 0; font-size: 12px; height: 40px; padding-left: 8px; }
.fnc-filter-field .el-input__icon { line-height: 40px; }
.fnc-year-filter { min-width: 130px; }

.fnc-view-toggle {
  align-items: center;
  background: #fff;
  border: 1px solid #d8dde5;
  border-radius: 8px;
  display: inline-flex;
  overflow: hidden;
  padding: 2px;
}

.fnc-view-toggle button {
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: 6px;
  color: #778397;
  cursor: pointer;
  display: inline-flex;
  font-size: 10px;
  font-weight: 600;
  gap: 6px;
  height: 30px;
  padding: 0 10px;
  transition: background .18s ease, color .18s ease, box-shadow .18s ease;
}

.fnc-view-toggle button:hover { color: var(--fnc-primary-dark); }

.fnc-view-toggle button.is-active {
  background: var(--fnc-primary);
  box-shadow: 0 3px 8px rgba(249, 99, 50, .22);
  color: #fff;
}

.fnc-view-toggle--toolbar {
  flex: 0 0 auto;
  height: 40px;
}

.fnc-view-toggle--toolbar button { height: 34px; }

.fnc-toolbar-icon-action {
  flex: 0 0 40px;
  height: 40px;
  min-height: 40px;
  min-width: 40px;
  padding: 0;
  width: 40px;
}

.fnc-toolbar-icon-action:disabled {
  cursor: wait;
  opacity: .65;
}

.fnc-table-shell {
  background: #fff;
  border: 1px solid var(--fnc-border);
  border-radius: 12px;
  box-shadow: 0 6px 18px rgba(35, 48, 68, .045);
  margin-bottom: 14px;
  overflow: hidden;
}

.fnc-table-scroll {
  overflow-x: auto;
  scrollbar-color: #cfd6df transparent;
  scrollbar-width: thin;
}

.fnc-table-scroll:focus {
  outline: 2px solid rgba(249, 99, 50, .32);
  outline-offset: -2px;
}

.fnc-table {
  border-collapse: separate;
  border-spacing: 0;
  color: #4b586d;
  font-size: 11px;
  margin: 0;
  min-width: 1260px;
  table-layout: fixed;
  width: 100%;
}

.fnc-table th {
  background: #f7f9fc;
  border-bottom: 1px solid #e5e9ef;
  color: #7d899a;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: .045em;
  padding: 11px 12px;
  text-align: left;
  text-transform: uppercase;
  white-space: nowrap;
}

.fnc-table td {
  background: #fff;
  border-bottom: 1px solid #edf0f4;
  height: 66px;
  padding: 10px 12px;
  vertical-align: middle;
}

.fnc-table tbody tr:last-child td { border-bottom: 0; }
.fnc-table tbody tr:hover td { background: #fffaf8; }

.fnc-table th:nth-child(1) { width: 105px; }
.fnc-table th:nth-child(2) { width: 105px; }
.fnc-table th:nth-child(3) { width: 205px; }
.fnc-table th:nth-child(4) { width: 145px; }
.fnc-table th:nth-child(5) { width: 145px; }
.fnc-table th:nth-child(6) { width: 110px; }
.fnc-table th:nth-child(7) { width: 240px; }
.fnc-table th:nth-child(8) { width: 135px; }
.fnc-table th:nth-child(9) { width: 185px; }

.fnc-table th:last-child,
.fnc-table td:last-child {
  box-shadow: -8px 0 14px -14px rgba(35, 48, 68, .5);
  position: sticky;
  right: 0;
}

.fnc-table th:last-child { z-index: 3; }
.fnc-table td:last-child { z-index: 2; }

.fnc-table-reference button {
  background: none;
  border: 0;
  color: var(--fnc-ink);
  cursor: pointer;
  display: block;
  font-size: 11px;
  font-weight: 700;
  padding: 0;
}

.fnc-table-reference button:hover { color: var(--fnc-primary); }

.fnc-table-reference small {
  color: var(--fnc-primary-dark);
  display: block;
  font-size: 9px;
  font-weight: 700;
  margin-top: 4px;
}

.fnc-table-product strong,
.fnc-table-product small,
.fnc-table-date strong,
.fnc-table-date small {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.fnc-table-product strong,
.fnc-table-date strong {
  color: #354155;
  font-size: 11px;
}

.fnc-table-product small,
.fnc-table-date small {
  color: #8994a5;
  font-size: 9px;
  margin-top: 4px;
}

.fnc-table-product small {
  color: var(--fnc-primary-dark);
  font-weight: 700;
  text-transform: uppercase;
}

.fnc-table-cause span {
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  display: -webkit-box;
  line-height: 1.45;
  overflow: hidden;
}

.fnc-badge--minor { background: #f1f4f8; color: #627086; }

.fnc-table-actions {
  flex-wrap: nowrap;
  justify-content: flex-end;
}

.fnc-table .fnc-table-actions { gap: 4px; }

.fnc-table .fnc-table-actions button {
  height: 28px;
  width: 28px;
}

.fnc-table-skeleton {
  height: 10px;
  width: 100%;
}

.fnc-table--loading td { height: 58px; }
.fnc-card-grid { margin-left: -7px; margin-right: -7px; }
.fnc-card-column { display: flex; padding: 0 7px 14px; }

.fnc-result-card {
  background: var(--fnc-surface);
  border: 1px solid var(--fnc-border);
  border-radius: 12px;
  box-shadow: 0 6px 18px rgba(35, 48, 68, .055);
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 384px;
  overflow: hidden;
  transition: border-color .2s ease, box-shadow .2s ease, transform .2s ease;
}

.fnc-result-card:not(.fnc-result-card--skeleton):hover {
  border-color: #f3c2b2;
  box-shadow: 0 11px 25px rgba(35, 48, 68, .09);
  transform: translateY(-2px);
}

.fnc-card-header {
  border-bottom: 1px solid #edf0f4;
  justify-content: space-between;
  min-height: 54px;
  padding: 11px 14px;
}

.fnc-reference {
  background: none;
  border: 0;
  color: var(--fnc-ink);
  cursor: pointer;
  font-size: 13px;
  font-weight: 700;
  padding: 0;
}

.fnc-reference:hover { color: var(--fnc-primary); }
.fnc-card-badges { align-items: center; display: flex; gap: 5px; }

.fnc-badge {
  align-items: center;
  border-radius: 20px;
  display: inline-flex;
  font-size: 9px;
  font-weight: 700;
  gap: 4px;
  letter-spacing: .02em;
  padding: 5px 7px;
}

.fnc-badge--open { background: #fff5df; color: #9b6500; }
.fnc-badge--closed { background: #eaf8ef; color: #247944; }
.fnc-badge--major { background: #fff0f0; color: #bc3939; }

.fnc-card-content {
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: 15px 14px 13px;
}

.fnc-overline {
  color: var(--fnc-primary-dark);
  font-size: 9px;
  font-weight: 700;
  letter-spacing: .07em;
  overflow: hidden;
  text-overflow: ellipsis;
  text-transform: uppercase;
  white-space: nowrap;
}

.fnc-card-content h3 {
  color: var(--fnc-ink);
  font-size: 15px;
  font-weight: 700;
  line-height: 1.3;
  margin: 5px 0 1px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.fnc-product-reference {
  color: #929cad;
  font-size: 10px;
  margin: 0;
}

.fnc-meta-grid {
  display: grid;
  gap: 11px 8px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin: 18px 0;
}

.fnc-meta-item { align-items: center; display: flex; gap: 8px; min-width: 0; }
.fnc-meta-item > i { color: #a3adbb; font-size: 11px; text-align: center; width: 13px; }
.fnc-meta-item span { display: flex; flex-direction: column; min-width: 0; }
.fnc-meta-item small { color: #9aa4b4; font-size: 8px; line-height: 1.25; text-transform: uppercase; }
.fnc-meta-item strong { color: #4b586d; font-size: 10px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

.fnc-cause-block {
  background: #f8f9fb;
  border-left: 3px solid #f7b49e;
  border-radius: 0 7px 7px 0;
  margin-top: auto;
  min-height: 66px;
  padding: 9px 10px;
}

.fnc-cause-block span { color: #929cad; font-size: 8px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; }
.fnc-cause-block p {
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  color: #586579;
  display: -webkit-box;
  font-size: 10px;
  line-height: 1.5;
  margin: 3px 0 0;
  overflow: hidden;
}

.fnc-card-footer {
  background: #fbfcfd;
  border-top: 1px solid #edf0f4;
  gap: 9px;
  justify-content: space-between;
  min-height: 64px;
  padding: 10px 12px;
}

.fnc-created-by { gap: 7px; min-width: 0; }
.fnc-avatar {
  align-items: center;
  background: #edf2f8;
  border-radius: 50%;
  color: #607087;
  display: inline-flex;
  flex: 0 0 29px;
  font-size: 9px;
  font-weight: 700;
  height: 29px;
  justify-content: center;
}

.fnc-created-by > span:last-child { display: flex; flex-direction: column; min-width: 0; }
.fnc-created-by small { color: #9aa4b4; font-size: 7px; white-space: nowrap; }
.fnc-created-by strong { color: #647186; font-size: 9px; max-width: 78px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.fnc-card-actions { display: flex; gap: 3px; }

.fnc-card-actions button {
  align-items: center;
  background: #fff;
  border: 1px solid #e4e8ee;
  border-radius: 6px;
  color: #778397;
  cursor: pointer;
  display: inline-flex;
  font-size: 9px;
  height: 27px;
  justify-content: center;
  padding: 0;
  transition: .18s ease;
  width: 27px;
}

.fnc-card-actions button:hover,
.fnc-card-actions button:focus {
  background: var(--fnc-primary);
  border-color: var(--fnc-primary);
  color: #fff;
}

.fnc-state-card {
  align-items: center;
  background: #fff;
  border: 1px dashed #dce2e9;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 330px;
  padding: 30px;
  text-align: center;
}

.fnc-state-card > span {
  align-items: center;
  background: #f5f7fa;
  border-radius: 50%;
  color: #9aa5b5;
  display: inline-flex;
  font-size: 20px;
  height: 58px;
  justify-content: center;
  margin-bottom: 14px;
  width: 58px;
}

.fnc-state-card h3 { color: var(--fnc-ink); font-size: 17px; font-weight: 700; margin: 0 0 5px; }
.fnc-state-card .btn { margin-top: 17px; }
.fnc-state-card--error > span { background: #fff2ed; color: var(--fnc-primary); }

.fnc-skeleton {
  background: #edf0f4;
  border-radius: 5px;
  display: block;
  overflow: hidden;
  position: relative;
}

.fnc-skeleton::after {
  animation: fnc-skeleton-shimmer 1.5s infinite;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, .75), transparent);
  content: '';
  inset: 0;
  position: absolute;
  transform: translateX(-100%);
}

@keyframes fnc-skeleton-shimmer { 100% { transform: translateX(100%); } }
.fnc-skeleton--reference { height: 13px; width: 76px; }
.fnc-skeleton--badge { border-radius: 20px; height: 22px; width: 74px; }
.fnc-skeleton--label { height: 8px; width: 42%; }
.fnc-skeleton--title { height: 16px; margin-top: 8px; width: 82%; }
.fnc-skeleton--subtitle { height: 9px; margin-top: 6px; width: 36%; }
.fnc-skeleton--meta { height: 28px; }
.fnc-skeleton--cause { height: 10px; margin-top: auto; width: 100%; }
.fnc-skeleton--cause-short { height: 10px; margin-top: 8px; width: 68%; }
.fnc-skeleton--date { height: 28px; width: 96px; }
.fnc-skeleton-actions { display: flex; gap: 3px; }
.fnc-skeleton--action { height: 27px; width: 27px; }

.fnc-email-dialog { max-width: 720px !important; }
.fnc-email-dialog .modal-content { border: 0; border-radius: 14px; box-shadow: 0 25px 65px rgba(26, 36, 51, .24); overflow: hidden; }
.fnc-email-header { align-items: center !important; border-bottom: 1px solid var(--fnc-border) !important; justify-content: flex-start !important; min-height: 82px; padding: 18px 24px !important; }
.fnc-email-header .close { margin-left: auto; order: 2; }
.fnc-email-heading { align-items: center; display: flex; gap: 13px; }
.fnc-email-heading-icon {
  align-items: center;
  background: #fff1ec;
  border-radius: 10px;
  color: var(--fnc-primary);
  display: inline-flex;
  flex: 0 0 44px;
  height: 44px;
  justify-content: center;
}

.fnc-email-heading h4 { color: var(--fnc-ink); font-size: 17px; font-weight: 700; margin: 0 0 3px; }
.fnc-email-heading p { color: var(--fnc-muted); font-size: 10px; margin: 0; }
.fnc-email-body { padding: 22px 24px 24px !important; }
.fnc-email-form { display: flex; flex-direction: column; gap: 18px; }
.fnc-form-group { margin: 0; }
.fnc-label-row { justify-content: space-between; margin-bottom: 7px; }
.fnc-label-row label { color: #3f4c60; font-size: 11px; font-weight: 700; margin: 0; }
.fnc-label-row label small { color: #8b96a7; font-size: 9px; font-weight: 400; }
.fnc-label-row > span { color: #9aa4b4; font-size: 9px; }
.fnc-recipient-select { width: 100%; }
.fnc-recipient-select .el-input__inner,
.fnc-recipient-select .el-select__tags + .el-input .el-input__inner { min-height: 44px; }
.fnc-recipient-select .el-input__inner,
.fnc-form-control {
  border: 1px solid #dfe4ea;
  border-radius: 8px;
  color: var(--fnc-ink);
  font-size: 12px;
  transition: border-color .2s ease, box-shadow .2s ease;
}

.fnc-recipient-select .el-input.is-focus .el-input__inner,
.fnc-form-control:focus {
  border-color: #f5a489;
  box-shadow: 0 0 0 3px rgba(249, 99, 50, .09);
  outline: none;
}

.fnc-recipient-select .el-tag { background: #fff1ec; border-color: #ffd4c5; color: #b84924; }
.fnc-field-help { color: #8c97a8; font-size: 9px; margin: 6px 0 0; }
.fnc-field-help i { color: #f29a7d; margin-right: 3px; }
.fnc-email-privacy {
  align-items: flex-start;
  background: #f4f8fd;
  border: 1px solid #e3edf8;
  border-radius: 9px;
  color: #58708d;
  display: flex;
  gap: 10px;
  padding: 11px 13px;
}

.fnc-email-privacy > i { color: #4776a8; margin-top: 2px; }
.fnc-email-privacy strong { color: #405873; display: block; font-size: 10px; margin-bottom: 2px; }
.fnc-email-privacy p { font-size: 9px; line-height: 1.45; margin: 0; }
.fnc-form-control { display: block; padding: 10px 12px; width: 100%; }
input.fnc-form-control { height: 42px; }
textarea.fnc-form-control { min-height: 112px; resize: vertical; }
.fnc-email-actions { border-top: 1px solid #edf0f4; gap: 9px; justify-content: flex-end; margin-top: 2px; padding-top: 17px; }
.fnc-email-actions .fnc-primary-action { min-width: 155px; }

@media (max-width: 1199px) {
  .fnc-toolbar-card { align-items: stretch; flex-direction: column; }
  .fnc-toolbar-actions { justify-content: flex-end; }
}

@media (max-width: 991px) {
  .fnc-summary-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .fnc-toolbar-main { flex-wrap: wrap; }
  .fnc-search-field { flex-basis: 100%; }
  .fnc-filter-field { flex: 1; }
}

@media (max-width: 575px) {
  .fnc-page-header { align-items: flex-start; flex-direction: column; }
  .fnc-page-header .fnc-primary-action { width: 100%; }
  .fnc-page-header h1 { font-size: 23px; }
  .fnc-summary-grid { gap: 8px; }
  .fnc-summary-card { min-height: 45px; padding: 11px; }
  .fnc-summary-icon { height: 37px; width: 37px; }
  .fnc-toolbar-main { align-items: stretch; flex-direction: column; }
  .fnc-search-field,
  .fnc-filter-field { flex: auto; min-width: 100%; }
  .fnc-toolbar-actions { display: grid; grid-template-columns: repeat(2, 1fr); }
  .fnc-toolbar-actions .fnc-secondary-action:first-child:last-child { grid-column: 1 / -1; }
  .fnc-email-dialog { margin: 12px !important; }
  .fnc-email-header { padding: 16px !important; }
  .fnc-email-body { padding: 18px 16px !important; }
  .fnc-email-actions { align-items: stretch; flex-direction: column-reverse; }
  .fnc-email-actions .btn { width: 100%; }
}

/* Ajustements de la liste : alignement sur la direction artistique du dashboard. */
.fnc-list-page {
  z-index: auto;
  position: relative;
  margin-top: -20px;
  padding-top: 0;
}

.fnc-list-shell {
  background: #fff;
  border-radius: 6px;
  display: block;
  margin-bottom: 20px;
  overflow: visible;
}

.fnc-list-header {
  border-bottom: 1px solid #eceff3 !important;
  padding: 18px 20px 15px !important;
}

.fnc-title-row,
.fnc-title-copy,
.fnc-list-toolbar,
.fnc-toolbar-actions,
.fnc-card-fact,
.fnc-history-row {
  align-items: center;
  display: flex;
}

.fnc-title-row {
  gap: 16px;
  justify-content: space-between;
  margin-bottom: 14px;
}

.fnc-title-copy {
  flex-wrap: wrap;
  gap: 9px;
}

.fnc-title-copy .card-title {
  color: #252a31;
  font-size: 18px;
  font-weight: 800;
  line-height: 1.2;
  margin: 0;
}

.fnc-list-count {
  align-items: center;
  background: #fff3ef;
  border: 1px solid #ffd5c8;
  border-radius: 999px;
  color: #9a3412;
  display: inline-flex;
  font-size: 11px;
  font-weight: 700;
  gap: 4px;
  height: 26px;
  padding: 0 10px;
}

.fnc-list-count strong {
  color: #f96332;
  font-size: 14px;
}

.fnc-add-button {
  align-items: center;
  display: inline-flex;
  gap: 7px;
  margin: 0;
  text-transform: none;
}

.fnc-list-toolbar {
  display: grid;
  gap: 9px;
  grid-template-columns: minmax(230px, 1fr) minmax(165px, 190px) minmax(115px, 135px) auto;
}

.fnc-list-toolbar .fnc-search-field {
  background: #fff;
  border: 0;
  border-radius: 8px;
  min-width: 0;
  padding: 0;
  position: relative;
}

.fnc-list-toolbar .fnc-search-field:focus-within {
  box-shadow: none;
}

.fnc-list-toolbar .fnc-search-field > i {
  color: #98a2b3;
  left: 12px;
  pointer-events: none;
  position: absolute;
  z-index: 1;
}

.fnc-list-toolbar .fnc-search-field input {
  background: #fff;
  border: 1px solid #d3d3d3;
  border-radius: 8px;
  color: #2c2c2c;
  font-size: 13px;
  height: 36px;
  padding: 0 34px;
  width: 100%;
}

.fnc-list-toolbar .fnc-search-field input:focus {
  border-color: #f96332;
  box-shadow: none;
}

.fnc-list-toolbar .fnc-clear-search {
  color: #98a2b3;
  position: absolute;
  right: 6px;
}

.fnc-toolbar-card .fnc-filter-field--element {
  background: #fff;
  border: 1px solid #d8dde5;
  border-radius: 8px;
  height: 40px;
  min-width: 155px;
  padding: 0;
  position: relative;
}

.fnc-toolbar-card .fnc-filter-field--element > span {
  color: #8b95a5;
  flex: 0 0 auto;
  font-size: 9px;
  font-weight: 600;
  padding-left: 11px;
  text-transform: uppercase;
}

.fnc-toolbar-card .fnc-filter-field--element .el-select {
  flex: 1;
  min-width: 0;
}

.fnc-toolbar-card .fnc-filter-field--element .el-input {
  margin: 0 !important;
}

.fnc-toolbar-card .fnc-filter-field--element .el-input__inner {
  background: transparent !important;
  border: 0 !important;
  border-radius: 0 !important;
  box-shadow: none !important;
  color: #2c2c2c !important;
  font-size: 12px;
  font-weight: 400 !important;
  height: 38px !important;
  line-height: 38px !important;
  margin: 0 !important;
  min-width: 0;
  padding: 0 29px 0 8px !important;
  text-align: left !important;
  text-transform: none !important;
}

.fnc-toolbar-card .fnc-filter-field--element:focus-within {
  border-color: #f96332;
}

.fnc-toolbar-card .fnc-filter-field--element .el-input__icon {
  line-height: 38px !important;
}

.fnc-toolbar-card .fnc-filter-field--element .el-select__caret {
  color: #f96332;
}

.fnc-list-toolbar .fnc-toolbar-actions {
  gap: 6px;
  justify-content: flex-end;
}

.fnc-toolbar-button {
  align-items: center;
  background: #fff;
  border: 1px solid #d8dde5;
  border-radius: 8px;
  box-shadow: none;
  color: #525f73;
  display: inline-flex;
  font-size: 11px;
  font-weight: 600;
  gap: 6px;
  height: 36px;
  justify-content: center;
  margin: 0;
  padding: 0 11px;
  text-transform: none;
  white-space: nowrap;
}

.fnc-toolbar-button i {
  color: #f96332;
}

.fnc-toolbar-button:hover,
.fnc-toolbar-button:focus {
  background: #fff7f4;
  border-color: #f96332;
  color: #b9431c;
}

.fnc-list-body {
  padding: 15px 20px 20px !important;
}

.fnc-card-grid {
  margin-left: -7px;
  margin-right: -7px;
}

.fnc-result-card {
  border: 1px solid #e1e5ea;
  border-radius: 9px;
  box-shadow: 0 3px 12px rgba(44, 44, 44, .055);
  min-height: 455px;
}

.fnc-result-card:not(.fnc-result-card--skeleton):hover {
  border-color: #f0b19b;
  box-shadow: 0 7px 18px rgba(44, 44, 44, .09);
  transform: none;
}

.fnc-card-header {
  background: #fff;
  min-height: 55px;
  padding: 12px 14px;
}

.fnc-reference {
  color: #2c2c2c;
  font-size: 14px;
  font-weight: 800;
}

.fnc-card-badges {
  flex-wrap: wrap;
  justify-content: flex-end;
}

.fnc-badge {
  font-size: 10px;
  padding: 5px 8px;
}

.fnc-badge--cost {
  background: #fff3ef;
  color: #a84625;
}

.fnc-card-content {
  padding: 16px 15px 14px;
}

.fnc-overline {
  color: #f96332;
  font-size: 10px;
  letter-spacing: .045em;
}

.fnc-card-content h3 {
  color: #2c2c2c;
  font-size: 16px;
  margin: 5px 0 2px;
}

.fnc-product-reference {
  color: #8490a2;
  font-size: 11px;
}

.fnc-card-facts {
  background: #f8f9fb;
  border: 1px solid #eceff3;
  border-radius: 8px;
  display: grid;
  gap: 0;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin: 16px 0 12px;
  overflow: hidden;
}

.fnc-card-fact {
  gap: 9px;
  min-width: 0;
  padding: 10px 11px;
}

.fnc-card-fact:not(.fnc-card-fact--wide):last-child {
  border-left: 1px solid #e5e9ee;
}

.fnc-card-fact--wide {
  border-bottom: 1px solid #e5e9ee;
  grid-column: 1 / -1;
}

.fnc-fact-icon {
  align-items: center;
  background: #fff;
  border: 1px solid #e5e9ee;
  border-radius: 6px;
  color: #f96332;
  display: inline-flex;
  flex: 0 0 28px;
  height: 28px;
  justify-content: center;
  width: 28px;
}

.fnc-card-fact > span:last-child {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.fnc-card-fact small {
  color: #8a95a5;
  font-size: 9px;
  margin-bottom: 2px;
  text-transform: uppercase;
}

.fnc-card-fact strong {
  color: #465266;
  font-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.fnc-cause-block {
  background: #fff;
  border: 1px solid #eceff3;
  border-left: 3px solid #f96332;
  min-height: 69px;
  padding: 10px 11px;
}

.fnc-cause-block span {
  color: #7f8999;
  font-size: 9px;
}

.fnc-cause-block p {
  color: #4d596c;
  font-size: 12px;
  line-height: 1.5;
}

.fnc-history-block {
  border-top: 1px solid #eceff3;
  display: grid;
  gap: 8px;
  grid-template-columns: 1fr;
  margin-top: 13px;
  padding-top: 12px;
}

.fnc-history-row {
  gap: 8px;
  min-width: 0;
}

.fnc-history-icon {
  align-items: center;
  background: #fff3ef;
  border-radius: 50%;
  color: #f96332;
  display: inline-flex;
  flex: 0 0 28px;
  font-size: 9px;
  height: 28px;
  justify-content: center;
}

.fnc-history-row--closed .fnc-history-icon {
  background: #eaf7ee;
  color: #31834e;
}

.fnc-history-row > span:last-child {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.fnc-history-row small {
  color: #8a95a5;
  font-size: 9px;
  white-space: nowrap;
}

.fnc-history-row strong {
  color: #505d70;
  font-size: 11px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.fnc-card-footer {
  background: #fafbfc;
  justify-content: flex-end;
  min-height: 58px;
  padding: 10px 14px;
}

.fnc-card-actions {
  gap: 6px;
}

.fnc-card-actions button {
  border-radius: 7px;
  color: #667386;
  font-size: 10px;
  height: 32px;
  width: 32px;
}

.fnc-card-actions button:hover,
.fnc-card-actions button:focus {
  background: #f96332;
  border-color: #f96332;
}

.fnc-result-card--skeleton .fnc-card-content {
  min-height: 342px;
}

.fnc-skeleton--label { height: 9px; width: 38%; }
.fnc-skeleton--title { height: 18px; margin-top: 9px; width: 78%; }
.fnc-skeleton--subtitle { height: 10px; margin-top: 7px; width: 34%; }
.fnc-skeleton-facts { display: grid; gap: 8px; grid-template-columns: 1fr 1fr; margin: 17px 0 13px; }
.fnc-skeleton--fact { height: 54px; }
.fnc-skeleton--cause { height: 12px; margin-top: 0; }
.fnc-skeleton--cause-short { height: 12px; margin-top: 8px; }
.fnc-skeleton-history { display: grid; gap: 8px; grid-template-columns: 1fr; margin-top: 22px; }
.fnc-skeleton--history { height: 31px; }
.fnc-skeleton-actions { margin-left: auto; }
.fnc-skeleton--action { height: 32px; width: 32px; }

/* Le thème global transforme les el-select en gros boutons ; la modale garde ici un champ clair. */
.fnc-list-page .fnc-recipient-select .el-input__inner {
  background: #fff !important;
  border: 1px solid #dfe4ea !important;
  box-shadow: none !important;
  color: #2c2c2c !important;
  font-weight: 400 !important;
  text-transform: none !important;
}

.fnc-list-page .fnc-recipient-select .el-select__caret {
  color: #f96332 !important;
}

.fnc-list-page .fnc-recipient-select .el-select__tags .el-tag {
  background: #fff1ec !important;
  border: 1px solid #ffd4c5 !important;
  color: #b84924 !important;
  margin-top: 4px;
  padding: 0 7px;
}

@media (max-width: 1199px) {
  .fnc-list-toolbar {
    grid-template-columns: minmax(220px, 1fr) minmax(155px, 180px) 120px;
  }

  .fnc-list-toolbar .fnc-toolbar-actions {
    grid-column: 1 / -1;
  }
}

@media (max-width: 767px) {
  .fnc-list-page {
    margin-top: -12px;
    padding-top: 0;
  }

  .fnc-list-header,
  .fnc-list-body {
    padding-left: 14px !important;
    padding-right: 14px !important;
  }

  .fnc-list-toolbar {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .fnc-list-toolbar .fnc-search-field {
    grid-column: 1 / -1;
  }

  .fnc-list-toolbar .fnc-toolbar-actions {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .fnc-toolbar-button {
    width: 100%;
  }
}

@media (max-width: 575px) {
  .fnc-title-row {
    align-items: stretch;
    flex-direction: column;
  }

  .fnc-add-button {
    justify-content: center;
    width: 100%;
  }

  .fnc-list-toolbar {
    grid-template-columns: 1fr;
  }

  .fnc-list-toolbar .fnc-search-field,
  .fnc-list-toolbar .fnc-toolbar-actions {
    grid-column: auto;
  }

  .fnc-history-block {
    grid-template-columns: 1fr;
  }
}

/* Choix explicite de la langue du PDF. */
.fnc-pdf-language-group {
  align-items: stretch;
  background: #fff;
  border: 1px solid #d8dde5;
  border-radius: 9px;
  display: inline-flex;
  height: 40px;
  overflow: hidden;
}

.fnc-pdf-language-label {
  align-items: center;
  background: #f7f8fa;
  border-right: 1px solid #d8dde5;
  color: #667386;
  display: inline-flex;
  font-size: 10px;
  font-weight: 700;
  gap: 6px;
  padding: 0 10px;
}

.fnc-pdf-language-label i {
  color: #f96332;
}

.fnc-pdf-language-group button {
  background: #fff;
  border: 0;
  border-right: 1px solid #e5e8ed;
  color: #657286;
  cursor: pointer;
  font-size: 10px;
  font-weight: 700;
  min-width: 40px;
  padding: 0 9px;
  transition: background-color .16s ease, color .16s ease;
}

.fnc-pdf-language-group button:last-child {
  border-right: 0;
}

.fnc-pdf-language-group button:hover {
  background: #fff3ef;
  color: #c94d24;
}

.fnc-pdf-language-group button.active {
  background: #f96332;
  color: #fff;
}

.fnc-pdf-language-group--advanced {
  border-radius: 8px;
  height: 36px;
  width: 100%;
}

.fnc-pdf-language-group--advanced button {
  flex: 1;
  min-width: 0;
}

/* Gabarit fixe : chaque information commence à la même hauteur sur toutes les cartes. */
.fnc-result-card {
  border-top: 3px solid #f96332;
  min-height: 451px;
}

.fnc-card-header {
  align-items: flex-start;
  background: #fcfcfd;
  box-sizing: border-box;
  height: 45px;
  min-height: 45px;
  overflow: hidden;
}

.fnc-card-badges {
  align-content: flex-start;
  max-width: 70%;
  min-height: 42px;
}

.fnc-card-badges .fnc-badge {
  font-size: 9px;
  padding: 4px 6px;
}

.fnc-card-content {
  box-sizing: border-box;
  display: grid;
  flex: 1;
  gap: 11px;
  grid-template-rows: 46px 108px 82px 48px;
  padding: 14px 14px 13px;
}

.fnc-card-product {
  min-height: 0;
  overflow: hidden;
}

.fnc-card-product .fnc-overline,
.fnc-card-product h3 {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.fnc-card-facts {
  height: 108px;
  margin: 0;
}

.fnc-card-fact {
  padding-bottom: 8px;
  padding-top: 8px;
}

.fnc-cause-block {
  box-sizing: border-box;
  height: 82px;
  margin: 0;
  min-height: 82px;
  overflow: hidden;
}

.fnc-cause-block p {
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  display: -webkit-box;
  margin: 5px 0 0;
  max-height: 36px;
  overflow: hidden;
}

.fnc-history-block {
  box-sizing: border-box;
  gap: 6px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  height: 48px;
  margin: 0;
  min-height: 48px;
  overflow: hidden;
  padding-top: 8px;
}

.fnc-history-block .fnc-history-row {
  gap: 6px;
}

.fnc-history-block .fnc-history-icon {
  flex-basis: 24px;
  height: 24px;
}

.fnc-history-block .fnc-history-row small {
  font-size: 8px;
}

.fnc-history-block .fnc-history-row strong {
  font-size: 10px;
}

.fnc-card-footer {
  flex: 0 0 58px;
  height: 58px;
}

.fnc-result-card--skeleton .fnc-card-content {
  display: grid;
  grid-template-rows: 46px 108px 82px 48px;
  min-height: 0;
}

.fnc-card-product--skeleton .fnc-skeleton--title {
  margin-top: 9px;
}

.fnc-skeleton-facts {
  gap: 6px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  height: 108px;
  margin: 0;
}

.fnc-skeleton-facts .fnc-skeleton--fact {
  height: 48px;
}

.fnc-skeleton-facts .fnc-skeleton--fact:first-child {
  grid-column: 1 / -1;
}

.fnc-skeleton-cause-box {
  background: #f7f8fa;
  border-radius: 7px;
  box-sizing: border-box;
  height: 82px;
  padding: 15px 11px;
}

.fnc-skeleton-history {
  gap: 6px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  height: 48px;
  margin: 0;
}

/* Sélecteur de destinataires indépendant des styles globaux d'Element UI. */
.fnc-email-dialog .modal-content {
  overflow: visible;
}

.fnc-recipient-control {
  position: relative;
}

.fnc-recipient-picker {
  align-items: center;
  background: #fff;
  border: 1px solid #dfe4ea;
  border-radius: 8px;
  cursor: text;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  min-height: 46px;
  padding: 6px 9px;
  transition: border-color .2s ease, box-shadow .2s ease;
}

.fnc-recipient-picker.is-focused {
  border-color: #f5a489;
  box-shadow: 0 0 0 3px rgba(249, 99, 50, .09);
}

.fnc-recipient-picker input {
  background: transparent;
  border: 0;
  color: #3f4c60;
  flex: 1 1 190px;
  font-size: 12px;
  height: 28px;
  min-width: 150px;
  outline: 0;
  padding: 0 3px;
}

.fnc-recipient-picker input::placeholder {
  color: #9aa4b4;
}

.fnc-recipient-chip {
  align-items: center;
  background: #fff1ec;
  border: 1px solid #ffd4c5;
  border-radius: 6px;
  color: #a84625;
  display: inline-flex;
  font-size: 10px;
  gap: 5px;
  line-height: 1;
  max-width: 100%;
  min-height: 27px;
  padding: 4px 5px 4px 8px;
}

.fnc-recipient-chip > span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.fnc-recipient-chip button {
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: 50%;
  color: #bd5a38;
  display: inline-flex;
  flex: 0 0 19px;
  font-size: 8px;
  height: 19px;
  justify-content: center;
  padding: 0;
}

.fnc-recipient-chip button:hover,
.fnc-recipient-chip button:focus {
  background: #f96332;
  color: #fff;
  outline: 0;
}

.fnc-recipient-suggestions {
  background: #fff;
  border: 1px solid #dfe4ea;
  border-radius: 9px;
  box-shadow: 0 14px 34px rgba(35, 48, 68, .16);
  left: 0;
  margin-top: 6px;
  max-height: 230px;
  overflow-y: auto;
  padding: 5px;
  position: absolute;
  right: 0;
  top: 100%;
  z-index: 1100;
}

.fnc-recipient-suggestion {
  align-items: center;
  background: #fff;
  border: 0;
  border-radius: 7px;
  color: #465266;
  display: flex;
  gap: 9px;
  padding: 8px 9px;
  text-align: left;
  width: 100%;
}

.fnc-recipient-suggestion:hover,
.fnc-recipient-suggestion:focus {
  background: #fff4f0;
  color: #a84625;
  outline: 0;
}

.fnc-recipient-avatar {
  align-items: center;
  background: #fff1ec;
  border-radius: 50%;
  color: #c75129;
  display: inline-flex;
  flex: 0 0 30px;
  font-size: 9px;
  font-weight: 700;
  height: 30px;
  justify-content: center;
}

.fnc-recipient-suggestion > span:nth-child(2) {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
}

.fnc-recipient-suggestion strong,
.fnc-recipient-suggestion small {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.fnc-recipient-suggestion strong {
  font-size: 11px;
}

.fnc-recipient-suggestion small {
  color: #8a95a5;
  font-size: 9px;
  margin-top: 2px;
}

.fnc-recipient-suggestion > i {
  color: #f96332;
  font-size: 9px;
}

.fnc-recipient-empty {
  align-items: center;
  color: #7f8999;
  display: flex;
  font-size: 10px;
  gap: 8px;
  min-height: 44px;
  padding: 8px 10px;
}

.fnc-recipient-empty i {
  color: #f29a7d;
}

.fnc-recipient-empty strong {
  color: #4d596c;
}

@media (max-width: 575px) {
  .fnc-pdf-language-group {
    justify-self: stretch;
  }

  .fnc-pdf-language-label,
  .fnc-pdf-language-group button {
    flex: 1;
    justify-content: center;
  }

  .fnc-view-toggle--toolbar {
    grid-column: 1 / -1;
    justify-self: stretch;
  }

  .fnc-view-toggle--toolbar button { flex: 1; }

  .fnc-toolbar-icon-action { width: 100%; }
}
</style>
